import { type UseQueryResult, useQuery } from "@tanstack/react-query"
import { useEffect } from "react"

import { api } from "@/api/cliente"
import type { Aplicacion, CxcAbierta, Reparto } from "@/componentes/ingresos/tipos"
import { precioValido } from "@/componentes/ordenes/lineas"
import { aTasaMicro, entreTasa, porTasa } from "@/componentes/tesoreria/conversion"
import { aCentimos, monto as comoCifra, comoMonto } from "@/componentes/tesoreria/dinero"
import { comoFecha } from "@/lib/fechas"

/** La tasa tecleada, con coma o punto, como la lee el servidor. */
function tasaLimpia(texto: string): string {
  return texto.trim().replace(",", ".")
}

/**
 * Lo que un abono a la CxC consume del ingreso, en céntimos de la moneda del ingreso.
 *
 * La tasa es SIEMPRE bolívares por 1 USD: de dólares a bolívares se multiplica y al
 * revés se divide, con el mismo redondeo que `ingresos_contraparte.consumo` del
 * servidor. `null` si la tasa no vale.
 */
export function consumoDe(
  centimos: bigint,
  monedaCxc: string,
  monedaIngreso: string,
  tasa: string,
): bigint | null {
  if (monedaCxc === monedaIngreso) return centimos
  const micro = aTasaMicro(tasaLimpia(tasa))
  if (micro === null) return null
  return monedaCxc === "USD" ? porTasa(centimos, micro) : entreTasa(centimos, micro)
}

/**
 * Las aplicaciones válidas de un reparto tecleado, o `null` si alguna cifra no vale, se
 * pasa de su CxC o le falta la tasa. Un abono vacío o en cero es «esta no».
 */
export function aplicacionesDe(
  reparto: Reparto,
  abiertas: readonly CxcAbierta[],
  monedaIngreso: string | undefined,
): Aplicacion[] | null {
  const salida: Aplicacion[] = []
  for (const c of abiertas) {
    const texto = (reparto[c.id]?.monto ?? "").trim()
    if (texto === "" || /^0+([.,]0*)?$/.test(texto)) continue
    const valido = precioValido(texto)
    if (valido === null || aCentimos(valido) > aCentimos(c.saldo)) return null
    if (monedaIngreso !== undefined && c.moneda !== monedaIngreso) {
      const tasa = tasaLimpia(reparto[c.id]?.tasa ?? "")
      if (aTasaMicro(tasa) === null) return null
      salida.push({ cxc_id: c.id, monto: valido, tasa })
    } else {
      salida.push({ cxc_id: c.id, monto: valido })
    }
  }
  return salida
}

/** Lo que consume del ingreso un reparto ya validado, en céntimos de su moneda. */
export function consumidoDe(
  aplicaciones: readonly Aplicacion[],
  abiertas: readonly CxcAbierta[],
  monedaIngreso: string,
): bigint {
  let total = 0n
  for (const a of aplicaciones) {
    const moneda = abiertas.find((x) => x.id === a.cxc_id)?.moneda ?? monedaIngreso
    total += consumoDe(aCentimos(a.monto), moneda, monedaIngreso, a.tasa ?? "") ?? 0n
  }
  return total
}

/**
 * Las CxC abiertas que el ingreso puede cobrar —también las de la otra moneda, con la
 * tasa BCV de `fecha`— y el reparto propuesto para `monto`.
 */
export function useCxcAbiertas(
  clienteId: number | null,
  divisa: string | undefined,
  monto: string | null,
  fecha?: string,
): UseQueryResult<CxcAbierta[]> {
  return useQuery({
    queryKey: ["cxc-abiertas", clienteId, divisa, monto, fecha],
    queryFn: () => {
      const q = new URLSearchParams({ cliente_id: String(clienteId), moneda: divisa ?? "" })
      if (monto) q.set("monto", monto)
      if (fecha) q.set("fecha", fecha)
      return api.get<CxcAbierta[]>(`/api/v1/ingresos/cxc-abiertas?${q}`)
    },
    enabled: clienteId !== null && divisa !== undefined,
  })
}

/**
 * Qué cuentas por cobrar del cliente cobra el ingreso, y por cuánto. **Es la puerta de
 * la cobranza** (15/10): el cobro se registra aquí, atado al dinero que entró.
 *
 * ⚠ **El reparto propuesto lo calcula el SERVIDOR**, primero las de la moneda del
 * ingreso y de la más antigua a la más reciente (`GET /ingresos/cxc-abiertas`), y se
 * corrige a mano. Lo que no se reparte queda de ANTICIPO del cliente.
 *
 * ⚠ **Una CxC de la OTRA moneda se cobra con su tasa**: se teclea cuánto se le abona
 * —en su moneda— y la tasa en bolívares por 1 USD (llega la del BCV del día del
 * ingreso), y se ve lo que consume del ingreso. Manda la tasa de la operación.
 */
export function RepartoCxc({
  abiertas,
  divisa,
  monto,
  reparto,
  onReparto,
}: {
  /** La de `useCxcAbiertas`: quien llama la necesita también para validar el envío. */
  abiertas: UseQueryResult<CxcAbierta[]>
  divisa: string
  /** Lo que queda del ingreso por repartir, ya validado. */
  monto: string | null
  reparto: Reparto
  onReparto: (reparto: Reparto) => void
}) {
  // Al llegar —o al cambiar el monto— se propone el reparto del servidor.
  useEffect(() => {
    if (!abiertas.data) return
    onReparto(
      Object.fromEntries(
        abiertas.data.map((c) => [
          c.id,
          {
            monto: aCentimos(c.sugerido) > 0n ? c.sugerido : "",
            tasa: c.tasa_sugerida ? String(Number(c.tasa_sugerida)) : "",
          },
        ]),
      ),
    )
  }, [abiertas.data, onReparto])

  if (abiertas.isPending) return <p className="text-sm">Buscando sus cuentas por cobrar…</p>
  if (abiertas.isError)
    return (
      <p className="text-sm" style={{ color: "var(--destructive)" }}>
        No se pudieron cargar sus cuentas por cobrar.
      </p>
    )
  const filas = abiertas.data
  if (filas.length === 0) {
    return (
      <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
        No tiene cuentas por cobrar abiertas en bolívares ni en dólares: todo queda de anticipo.
      </p>
    )
  }
  const aplicaciones = aplicacionesDe(reparto, filas, divisa)
  const repartido = aplicaciones ? consumidoDe(aplicaciones, filas, divisa) : 0n
  const total = monto ? aCentimos(monto) : 0n
  const anticipo = total - repartido

  return (
    <div className="space-y-2 text-sm">
      <ul className="space-y-2">
        {filas.map((c) => {
          const id = `reparto-${c.id}`
          const otra = c.moneda !== divisa
          const fila = reparto[c.id] ?? { monto: "", tasa: "" }
          const abono = precioValido(fila.monto)
          const consume =
            otra && abono !== null ? consumoDe(aCentimos(abono), c.moneda, divisa, fila.tasa) : null
          return (
            <li key={c.id} className="tarjeta space-y-2 p-3">
              <div className="flex flex-wrap items-center gap-3">
                <label htmlFor={id} className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{c.concepto}</span>
                  <span className="block text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
                    CxC {c.id} · debe {comoCifra(c.saldo)} {c.moneda}
                    {c.fecha_vencimiento ? ` · vence ${comoFecha(c.fecha_vencimiento)}` : ""}
                  </span>
                </label>
                <div className="flex items-center gap-1">
                  <input
                    id={id}
                    value={fila.monto}
                    onChange={(e) =>
                      onReparto({ ...reparto, [c.id]: { ...fila, monto: e.target.value } })
                    }
                    inputMode="decimal"
                    placeholder="0,00"
                    className="campo w-32 text-right tabular-nums"
                  />
                  {otra && <span className="text-xs">{c.moneda}</span>}
                </div>
              </div>
              {otra && (
                <div className="flex flex-wrap items-center justify-end gap-2 text-xs">
                  <label htmlFor={`${id}-tasa`} style={{ color: "var(--fg-muted)" }}>
                    Tasa (BS por 1 USD)
                  </label>
                  <input
                    id={`${id}-tasa`}
                    value={fila.tasa}
                    onChange={(e) =>
                      onReparto({ ...reparto, [c.id]: { ...fila, tasa: e.target.value } })
                    }
                    inputMode="decimal"
                    placeholder={c.tasa_sugerida ? "" : "Sin tasa BCV ese día"}
                    className="campo w-28 text-right tabular-nums"
                  />
                  <span className="tabular-nums" style={{ color: "var(--fg-muted)" }}>
                    {consume !== null ? `= ${comoMonto(consume)} ${divisa} del ingreso` : ""}
                  </span>
                </div>
              )}
            </li>
          )
        })}
      </ul>
      {aplicaciones === null ? (
        <p style={{ color: "var(--destructive)" }}>
          Hay una cifra o una tasa que no vale, o un abono que pasa de lo que debe esa cuenta.
        </p>
      ) : anticipo < 0n ? (
        <p style={{ color: "var(--destructive)" }}>
          Se reparten {comoMonto(repartido)} {divisa} y el ingreso es de {comoMonto(total)}.
        </p>
      ) : (
        <p className="tabular-nums" style={{ color: "var(--fg-muted)" }}>
          Cobra {comoMonto(repartido)} {divisa}
          {anticipo > 0n ? ` · quedan ${comoMonto(anticipo)} ${divisa} de anticipo` : ""}
        </p>
      )}
    </div>
  )
}
